import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTableType } from '../graphql/types';
import { createTeachingBlockVersionTableColumns } from '../utils/TeachingBlockVersionTableUtils';

type TeachingBlockVersionRoomsTableProps = {
  versionUuid: string | undefined;
  search: string;
  versionRoomUuids: string[];
  onRowSelection: (rowData: TeachingBlockVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  onRemove: (uuids: string[]) => void | Promise<void>;
};

export const TeachingBlockVersionRoomsTable = observer(
  ({
    onRowSelection,
    onShowLessonClick,
    versionUuid,
    search,
    versionRoomUuids,
    onRemove,
  }: TeachingBlockVersionRoomsTableProps) => {
    const { t } = useTranslation();

    const { sorting, saveSorting } = useColumnsSort('teachingBlockVersionRoomsTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const columns = useMemo(() => createTeachingBlockVersionTableColumns(t('rooms.name')), [t]);

    const store = useTeachingBlockStore();
    const { confirm, ConfirmationDialog } = useConfirm();

    // Räume, die noch auf einer (platzierten oder freien) Karte liegen, dürfen nicht entfernt werden.
    const usedRooms = useMemo(
      () => [
        ...store.placedCards.flatMap((c) => c.rooms.map((room) => room.uuid)),
        ...store.freeCards.flatMap((c) => c.rooms.map((room) => room.uuid)),
      ],
      [store.placedCards, store.freeCards],
    );

    const handleRemove = useCallback(
      async (uuids: string[]) => {
        const res = await confirm({
          title: t('timetableVersion.removeRooms', { count: uuids.length }),
          message: t('timetableVersion.removeRoomsDescription', { count: uuids.length }),
        });
        if (res) {
          await onRemove(uuids);
        }
      },
      [confirm, onRemove, t],
    );

    const createActionItems = useCallback(
      (row: Row<TeachingBlockVersionTableType>): DropdownMenuItem[] => {
        return [
          {
            label: t('teachingBlockVersion.editData'),
          },
          {
            label: t('teachingBlockVersion.test'),
          },
          {
            label: t('teachingBlockVersion.remove'),
            type: 'default',
            color: 'error',
            disabled: store.readonly || usedRooms.includes(row.original.uuid),
            onClick: async () => {
              await handleRemove([row.original.uuid]);
            },
          },
        ];
      },
      [handleRemove, t, store.readonly, usedRooms],
    );

    const roomsTableData: TeachingBlockVersionTableType[] = [];
    store.rooms.forEach((r) => {
      roomsTableData.push({
        color: {},
        count: 0,
        name: r.name,
        shortName: r.roomNumber ?? '',
        uuid: r.uuid,
      });
    });

    return (
      <>
        <Table<TeachingBlockVersionTableType>
          globalFilter={search}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={roomsTableData}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('teachingBlockVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  disabled={store.readonly}
                  trigger={
                    <Button
                      disabled={store.readonly}
                      hierarchy='secondary'
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={createActionItems(row)} />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
